import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@loader/ui';

import { generateDataInsertUrl } from '@/features/links/utils/urlGenerator';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

interface PostApiInstructionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tableId: string;
  tableName: string;
}

function CodeBlock({ code, label }: { code: string; label: string }) {
  const { copy, copiedLabel } = useCopyToClipboard({ showToast: false });
  const isCopied = copiedLabel === label;

  return (
    <div className="group relative">
      <pre className="bg-muted whitespace-pre-wrap break-all rounded-md p-3 pr-10 text-xs leading-relaxed">
        <code>{code}</code>
      </pre>
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-1.5 top-1.5 h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
        onClick={() => copy(code, label)}
      >
        {isCopied ? (
          <Check className="h-3 w-3 text-green-500" />
        ) : (
          <Copy className="text-muted-foreground h-3 w-3" />
        )}
      </Button>
    </div>
  );
}

function CopyInlineButton({ text, label }: { text: string; label: string }) {
  const { copy, copiedLabel } = useCopyToClipboard({ showToast: false });
  const isCopied = copiedLabel === label;

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-5 w-5 shrink-0"
      onClick={() => copy(text, label)}
    >
      {isCopied ? (
        <Check className="h-3 w-3 text-green-500" />
      ) : (
        <Copy className="text-muted-foreground h-3 w-3" />
      )}
    </Button>
  );
}

export function PostApiInstructionsDialog({
  open,
  onOpenChange,
  tableId,
  tableName,
}: PostApiInstructionsDialogProps) {
  const [tab, setTab] = useState('curl');
  const endpointUrl = generateDataInsertUrl(tableId);

  const curlExample = `curl -X POST '${endpointUrl}' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer YOUR_API_KEY' \\
  -d '{ "data": [{ "key": "value" }] }'`;

  const jsExample = `await fetch('${endpointUrl}', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer YOUR_API_KEY',
  },
  body: JSON.stringify({
    data: [{ key: 'value' }],
  }),
});`;

  const pythonExample = `import requests

requests.post(
    '${endpointUrl}',
    headers={'Authorization': 'Bearer YOUR_API_KEY'},
    json={'data': [{'key': 'value'}]},
)`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Push data to "{tableName}"</DialogTitle>
          <DialogDescription>
            POST a JSON body with a <code className="bg-muted rounded px-1 py-0.5 text-xs font-mono">data</code> array.
            The first request defines the schema.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-1 grid gap-4 text-sm">
          {/* Endpoint */}
          <div className="grid gap-1.5">
            <span className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Endpoint</span>
            <div className="bg-muted flex items-center gap-2 rounded-md px-3 py-2">
              <code className="min-w-0 flex-1 break-all text-xs">{endpointUrl}</code>
              <CopyInlineButton text={endpointUrl} label="url" />
            </div>
            <p className="text-muted-foreground text-xs">
              Pass a <span className="text-foreground font-medium">read_write</span> API key via <code className="text-[11px]">Authorization: Bearer</code> header.
            </p>
          </div>

          {/* Code examples */}
          <div className="grid gap-2">
            <span className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Example</span>
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="h-8">
                <TabsTrigger value="curl" className="text-xs px-3 h-6">curl</TabsTrigger>
                <TabsTrigger value="js" className="text-xs px-3 h-6">JavaScript</TabsTrigger>
                <TabsTrigger value="python" className="text-xs px-3 h-6">Python</TabsTrigger>
              </TabsList>
              <TabsContent value="curl" className="mt-2">
                <CodeBlock code={curlExample} label="curl" />
              </TabsContent>
              <TabsContent value="js" className="mt-2">
                <CodeBlock code={jsExample} label="js" />
              </TabsContent>
              <TabsContent value="python" className="mt-2">
                <CodeBlock code={pythonExample} label="python" />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
